import type { FamilyClassroom } from '@tadpoles/common'

import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import { replacePersistedCollection } from './persistence/replace-persisted-collection.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

export class ClassroomService extends UserScopedService implements ServiceLifecycle {
    readonly #localBacking: FamilyUserLocalBacking
    readonly #restoreFromBacking: boolean
    #classrooms = $state.raw<FamilyClassroom[]>([])

    public constructor(
        scope: UserScopedServiceScope,
        localBacking: FamilyUserLocalBacking,
        restoreFromBacking = true
    ) {
        super(scope)
        this.#localBacking = localBacking
        this.#restoreFromBacking = restoreFromBacking
    }

    public get classrooms(): FamilyClassroom[] {
        return this.#classrooms
    }

    public async initialize(): Promise<void> {
        if (!this.#restoreFromBacking) {
            return
        }

        const store = await this.#localBacking.getClassroomStore()
        this.hydrate(store === undefined ? [] : await store.findAll())
    }

    public resume(): void {}

    public suspend(): void {}

    public destroy(): void {
        this.hydrate([])
    }

    public hydrate(classrooms: FamilyClassroom[]): void {
        this.#classrooms = classrooms
    }

    public replaceAll(classrooms: FamilyClassroom[]): Promise<void> {
        this.hydrate(classrooms)
        const snapshot = this.#classrooms

        return this.enqueueCacheWrite(async () => {
            const store = await this.#localBacking.getClassroomStore()

            if (store !== undefined) {
                await replacePersistedCollection(store, snapshot, (classroom) => classroom.key)
            }
        })
    }

    public get(classroomKey: string | undefined): FamilyClassroom | null {
        if (classroomKey === undefined) {
            return null
        }

        return this.#classrooms.find((classroom) => classroom.key === classroomKey) ?? null
    }
}
